import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { timeout } from 'rxjs/operators';
import * as XLSX from 'xlsx';

import { SetupStructureProfitCenterStore,SetupBusinessStructureStoreSetupStore } from '../../../store';
import { HttpService, TreeService, BreadcrumbService, BusinessService, ConstantService } from '../../../shared/services';
import { autoFitColumns } from '../../../common-functions';

interface StoreReportGroup {
    storeID: string;
    storeName: string;
    summaryRows: { name: string; revenue: number; expenses: number; net: number }[];
    detailedGroups: { pctrName: string; txns: any[]; subtotal: any; visibleTxns: any[] }[];
    storeSummary: { revenue: number; expenses: number; net: number };
}

@Component({
    selector: 'app-consolidated-report',
    templateUrl: './consolidated-report.component.html',
    styleUrls: ['./consolidated-report.component.scss'],
})
export class ConsolidatedReportComponent implements OnInit, OnDestroy {
    reportType: 'summary' | 'detailed' = 'detailed';
    startDate: Date | null = (() => { const d = new Date(); d.setDate(d.getDate() - 3); return d; })();
    endDate: Date | null = new Date();
    profitCenters: any[] = [{ _id: 'ALL', xName: 'ALL' }];
    allPctrUnderBusiness: any[] = [];
    stores: any[] = [{ _id: 'ALL', xName: 'ALL' }];
    selectedPCtrIDs: string[] = ['ALL'];
    private prevPCtrIDs: string[] = ['ALL'];
    selectedStores: string[] = ['ALL'];
    private prevStoreIDs: string[] = ['ALL'];
    storeID: string = '';
    storeName: string = '';
    businessID: string = '';
    businessName: string = '';
    isLoading: boolean = false;
    isGenerated: boolean = false;
    errorMessage: string = '';
    loadingProgress: string = '';
    isPdfLoading: boolean = false;
    isCsvLoading: boolean = false;
    isExcelLoading: boolean = false;
    totalRecords: number = 0;
    isLargeDataset: boolean = false;
    readonly LARGE_DATASET_THRESHOLD = 5000;
    private fetchGeneration = 0;

    summaryRows: { name: string; revenue: number; expenses: number; net: number }[] = [];
    detailedGroups: { pctrName: string; txns: any[]; subtotal: any }[] = [];
    storeGroups: StoreReportGroup[] = [];

    @ViewChild('reportResults') reportResults: ElementRef;

    bsConfig = { dateInputFormat: 'MM/DD/YYYY', containerClass: 'theme-default' };

    private storeSub: Subscription;
    private businessSub: Subscription;

    constructor(
        private pctrStore: SetupStructureProfitCenterStore,
        private storeStore: SetupBusinessStructureStoreSetupStore,
        private httpService: HttpService,
        private treeService: TreeService,
        private breadcrumbService: BreadcrumbService,
        private businessService: BusinessService,
        private constantService: ConstantService,
    ) {}

    ngOnInit(): void {
        this.setBreadcrumb();
        this.storeSub = this.treeService.watchStoreID$.subscribe((id: string) => {
            this.storeID = id || '';
            this.isGenerated = false;
            this.summaryRows = [];
            this.detailedGroups = [];
            this.storeGroups = [];
            this.errorMessage = '';
            if (id) {
                // this.loadStoreName(id);
                this.loadProfitCenters(id);                
            } else {
                this.profitCenters = [{ _id: 'ALL', xName: 'ALL' }];
                this.selectedPCtrIDs = ['ALL'];
                this.prevPCtrIDs = ['ALL'];
            }
        });
        this.businessSub = this.businessService.watchBusinessID$.subscribe((id: string) => {
            this.businessID = id || '';
            this.isGenerated = false;
            this.summaryRows = [];
            this.detailedGroups = [];
            this.storeGroups = [];
            this.errorMessage = '';
            if (id && !this.isAllBusinesses()) {
                this.loadBusinessName(id);
                this.loadStores(id);
                this.loadAllProfitCenters(id);
            } else {
                this.businessName = '';
                this.profitCenters = [{ _id: 'ALL', xName: 'ALL' }];
            }
            this.selectedPCtrIDs = ['ALL'];
            this.prevPCtrIDs = ['ALL'];
        });
    }

    ngOnDestroy(): void {
        this.fetchGeneration++;
        if (this.storeSub) {
            this.storeSub.unsubscribe();
        }
        if (this.businessSub) {
            this.businessSub.unsubscribe();
        }
    }

    isAllBusinesses():boolean{
        return this.businessID.startsWith('$$$');
    }
    setBreadcrumb(): void {
        const breadCrumb: any = {};
        breadCrumb.root = 'Reports';
        breadCrumb.child = 'Consolidated Report';
        breadCrumb.subChild = '';
        breadCrumb.subChildLink = null;
        breadCrumb.grandChild = '';
        breadCrumb.rootList = [];
        this.breadcrumbService.setBreadCrumb(breadCrumb);
    }

    loadBusinessName(id: string): void {
        this.httpService.get('xbcBusinesses', id).subscribe(
            (res: any) => { this.businessName = res?.data?.xName || ''; },
            () => { this.businessName = ''; },
        );
    }

    loadStores(businessID: string): void {
        this.selectedStores = ['ALL'];
        this.prevStoreIDs = ['ALL'];
        this.storeStore.getAll({ xBusinessID: businessID,xStatus: 1,selectKey:'_id,xName' }).subscribe(
            (res: any) => {
                const active = res?.data || [];
                this.stores = [{ _id: 'ALL', xName: 'ALL' }, ...active];
            },
            () => {
                this.stores = [{ _id: 'ALL', xName: 'ALL' }];
            },
        );
    }
    loadProfitCenters(storeID: string): void {
        this.selectedPCtrIDs = ['ALL'];
        this.prevPCtrIDs = ['ALL'];
        this.pctrStore.getAll({ xBusinessID:this.businessID,xStoreID: storeID,xStatus: 1,selectKey:'_id,xName' }).subscribe(
            (res: any) => {
                const active = res?.data || [];
                this.profitCenters = [{ _id: 'ALL', xName: 'ALL' }, ...active];
            },
            () => {
                this.profitCenters = [{ _id: 'ALL', xName: 'ALL' }];
            },
        );
    }

    loadAllProfitCenters(businessID: string): void {
        this.fetchAllStorePages({ xBusinessID: businessID, xStatus: 1, selectKey: '_id,xName' }, 1, []);
    }
    private fetchAllStorePages(baseParams: any, page: number, accumulated: any[]): void {
        this.httpService.getAll('xbcpctrs', { ...baseParams, page }).subscribe(
            (res: any) => {
                const batch: any[] = res?.data || [];
                const total: number = res?.meta?.totalRecords || 0;
                const combined = accumulated.concat(batch);

                if (batch.length > 0 && combined.length < total) {
                    this.fetchAllStorePages(baseParams, page + 1, combined);
                } else {
                    this.allPctrUnderBusiness = combined;
                }
            },
            () => {
            },
        );
    }    

    onStoreChange(selected: string[]): void { 
        if (!selected || selected.length === 0) {
            this.selectedStores = ['ALL'];
        } else if (selected.includes('ALL')) {
            if (!this.prevStoreIDs.includes('ALL')) {
                this.selectedStores = ['ALL'];
            } else {
                this.selectedStores = selected.filter(id => id !== 'ALL');
            }
        }
        this.prevStoreIDs = [...this.selectedStores];

        if (this.selectedStores.length == 1 && this.selectedStores[0] != 'ALL') {
            this.loadProfitCenters(this.selectedStores[0]);
            const found = this.stores.find(s => s._id === this.selectedStores[0]);
            this.storeName = found ? found.xName : this.selectedStores[0];
        } else {
            this.selectedPCtrIDs = ['ALL'];
            this.profitCenters = [{ _id: 'ALL', xName: 'ALL' }];
            this.storeName = '';
        }

        if (this.isGenerated && !this.isLoading) {
            // this.generateReport();
        } else {
            this.isGenerated = false;
            this.summaryRows = [];
            this.detailedGroups = [];
            this.storeGroups = [];
        }
    }

    onProfitCenterChange(selected: string[]): void {
        if (!selected || selected.length === 0) {
            this.selectedPCtrIDs = ['ALL'];
        } else if (selected.includes('ALL')) {
            if (!this.prevPCtrIDs.includes('ALL')) {
                this.selectedPCtrIDs = ['ALL'];
            } else {
                this.selectedPCtrIDs = selected.filter(id => id !== 'ALL');
            }
        }
        this.prevPCtrIDs = [...this.selectedPCtrIDs];

        if (this.isGenerated && !this.isLoading) {
            // this.generateReport();
        } else {
            this.isGenerated = false;
            this.summaryRows = [];
            this.detailedGroups = [];
            this.storeGroups = [];
        }
    }

    get dateRangeInvalid(): boolean {
        if (!this.startDate || !this.endDate) { return false; }
        const diffMs = new Date(this.endDate).getTime() - new Date(this.startDate).getTime();
        return diffMs < 0 || diffMs > 366 * 24 * 60 * 60 * 1000;
    }

    get datesRequired(): boolean {
        return !this.startDate || !this.endDate;
    }

    generateReport(): void {
        if (this.datesRequired) { return; }
        if (this.dateRangeInvalid) { return; }
        this.errorMessage = '';
        this.isGenerated = false;
        this.summaryRows = [];
        this.detailedGroups = [];
        this.storeGroups = [];
        this.generateDetailed();
    }

    resetFilters(): void {
        this.fetchGeneration++;
        this.selectedStores = ['ALL'];
        this.prevStoreIDs = ['ALL'];
        this.selectedPCtrIDs = ['ALL'];
        this.prevPCtrIDs = ['ALL'];
        this.startDate = null;
        this.endDate = null;
        this.isLoading = false;
        this.isGenerated = false;
        this.isLargeDataset = false;
        this.totalRecords = 0;
        this.summaryRows = [];
        this.detailedGroups = [];
        this.storeGroups = [];
        this.errorMessage = '';
        this.loadingProgress = '';
    }

    generateDetailed(): void {
        this.fetchGeneration++;
        this.isLoading = true;
        this.isLargeDataset = false;
        this.totalRecords = 0;

        const params: any = {xIsVoided: false, xRspStatus: "success", hashData: true, filterField: 'xAuthDate', filterType: 'advancedDateFilter' };
        // const params: any = { hashData: true, filterField: 'xSettleDate',filterType:'advancedDateFilter' };
        const businessID = this.businessService.businessID.getValue();
        if (businessID) { params.xBusinessID = businessID; }

        if (!this.selectedStores.includes('ALL') && this.selectedStores.length > 0) {
            if (this.selectedStores.length === 1) {
                params.xStoreID = this.selectedStores[0];
            } else {
                params.storeIDs = this.selectedStores;
            }
        }

        if (this.selectedPCtrIDs.includes('ALL')) {
            // no PCTR filter
        } else if (this.selectedPCtrIDs.length === 1) {
            params.xPCtrID = this.selectedPCtrIDs[0];
        } else {
            params.pctrIDs = this.selectedPCtrIDs;
        }

        if (this.startDate) { params.startDate = this.startDate; }
        if (this.endDate) { params.endDate = this.endDate; }

        this.loadingProgress = 'Loading...';
        this.fetchAllPagesIterative(params, this.fetchGeneration);
    }

    private async fetchAllPagesIterative(baseParams: any, generation: number): Promise<void> {
        const all: any[] = [];
        let page = 1;

        try {
            while (true) {
                if (this.fetchGeneration !== generation) { return; }

                const res: any = await this.httpService.getAll('xpeSettledTxns', {
                    ...baseParams,
                    page,
                    limit: 400,
                    // selectKey: '_id,xTxnActTag,xInvNum,xCustNum,xBrandTag,xMaskedAcctNum,' +
                    //            'xMainAmt,xIncSurcAmt,xTipAmt,xTtlAmt,' +
                    //            'xAuthDate,xAuthTime,xSettleDate,xSettleTime,' +
                    //            'xStoreID,xPCtrID',
                })
                    .pipe(timeout(30000))
                    .toPromise();

                if (this.fetchGeneration !== generation) { return; }

                const batch: any[] = res?.data || [];
                const total: number = res?.meta?.totalRecords || 0;
                this.totalRecords = total;

                if (page === 1 && total > 100000) {
                    this.isLoading = false;
                    this.loadingProgress = '';
                    this.errorMessage = `Too many records (${total.toLocaleString()}). Please narrow your date range or filter by store / profit center.`;
                    return;
                }

                if (total > this.LARGE_DATASET_THRESHOLD) {
                    this.isLargeDataset = true;
                }

                all.push(...batch);

                if (batch.length === 0 || all.length >= total) { break; }

                this.loadingProgress = `Loaded ${all.length.toLocaleString()} / ${total.toLocaleString()} records, please wait...`;
                page++;
            }

            if (this.fetchGeneration !== generation) { return; }

            this.storeGroups = this.groupByStore(all);
            this.detailedGroups = this.storeGroups.flatMap(sg => sg.detailedGroups);
            this.summaryRows = this.storeGroups.flatMap(sg => sg.summaryRows);
            await this.constantService.updateTransactionData(all);

            if (this.fetchGeneration !== generation) { return; }

            this.isLoading = false;
            this.loadingProgress = '';
            this.isGenerated = true;

        } catch (e) {
            if (this.fetchGeneration !== generation) { return; }
            this.isLoading = false;
            this.loadingProgress = '';
            this.errorMessage = 'Report generation failed. Please try again.';
        }
    }

    groupByPctr(txns: any[]): { pctrName: string; txns: any[]; subtotal: any; visibleTxns: any[] }[] {
        const map = new Map<string, { pctrName: string; txns: any[]; subtotal: any; visibleTxns: any[] }>();
        txns.forEach((t: any) => {
            const key = t.xPCtrID || 'unknown';
            if (!map.has(key)) {
                const pctr = this.allPctrUnderBusiness.find((p: any) => p._id === key);
                map.set(key, { pctrName: pctr?.xName || t.xPCtrName || t.pctrName || key, txns: [], subtotal: null, visibleTxns: [] });
            }
            map.get(key).txns.push(t);
        });
        map.forEach((group) => {
            group.subtotal = {
                xMainAmt: this.sumField(group.txns, 'xMainAmt')*0.01,
                xIncSurcAmt: this.sumField(group.txns, 'xIncSurcAmt')*0.01,
                xTipAmt: this.sumField(group.txns, 'xTipAmt')*0.01,
                xTtlAmt: this.sumField(group.txns, 'xTtlAmt')*0.01,
            };
            group.subtotal.xMerAmt = group.subtotal.xTtlAmt - group.subtotal.xIncSurcAmt;
        });
        return [...map.values()];
    }

    groupByStore(txns: any[]): StoreReportGroup[] {
        const map = new Map<string, any[]>();
        txns.forEach((t: any) => {
            const key = t.xStoreID || 'unknown';
            if (!map.has(key)) { map.set(key, []); }
            map.get(key).push(t);
        });
        const groups: StoreReportGroup[] = [];
        map.forEach((storeTxns, storeID) => {
            const storeRecord = this.stores.find(s => s._id === storeID);
            const storeName = storeRecord?.xName || storeID;
            const summaryRows = this.computeSummaryRows(storeTxns);
            const detailedGroups = this.groupByPctr(storeTxns);
            const storeSummary = {
                revenue:  summaryRows.reduce((s, r) => s + r.revenue, 0),
                expenses: summaryRows.reduce((s, r) => s + r.expenses, 0),
                net:      summaryRows.reduce((s, r) => s + r.net, 0),
            };
            groups.push({ storeID, storeName, summaryRows, detailedGroups, storeSummary });
        });
        return groups;
    }

    sumField(arr: any[], field: string): number {
        return arr.reduce((s, item) => s + (parseFloat(item[field]) || 0), 0);
    }

    computeSummaryRows(txns: any[]): { name: string; revenue: number; expenses: number; net: number }[] {
        const map = new Map<string, { name: string; revenue: number; expenses: number }>();
        txns.forEach((t: any) => {
            const key = t.xPCtrID || 'unknown';
            if (!map.has(key)) {
                const pctr = this.allPctrUnderBusiness.find((p: any) => p._id === key);
                map.set(key, { name: pctr?.xName || key, revenue: 0, expenses: 0 });
            }
            const row = map.get(key);
            const amt = (parseFloat(t.xTtlAmt) || 0) * 0.01;
            if (t.xTxnActTag === 'txnact.purchase') {
                row.revenue += amt;
            } else if (t.xTxnActTag === 'txnact.return') {
                row.expenses += amt;
            }
        });
        return [...map.values()].map(r => ({ ...r, net: r.revenue - r.expenses }));
    }

    getSummaryGrandTotal(): { revenue: number; expenses: number; net: number } {
        return {
            revenue: this.summaryRows.reduce((s, r) => s + (r.revenue || 0), 0),
            expenses: this.summaryRows.reduce((s, r) => s + (r.expenses || 0), 0),
            net: this.summaryRows.reduce((s, r) => s + (r.net || 0), 0),
        };
    }

    getDetailedGrandTotal(): any {
        return {
            xMainAmt:    this.detailedGroups.reduce((s, g) => s + (g.subtotal.xMainAmt   || 0), 0),
            xIncSurcAmt: this.detailedGroups.reduce((s, g) => s + (g.subtotal.xIncSurcAmt || 0), 0),
            xTipAmt:     this.detailedGroups.reduce((s, g) => s + (g.subtotal.xTipAmt     || 0), 0),
            xTtlAmt:     this.detailedGroups.reduce((s, g) => s + (g.subtotal.xTtlAmt     || 0), 0),
            xMerAmt:     this.detailedGroups.reduce((s, g) => s + (g.subtotal.xMerAmt     || 0), 0),
        };
    }

    getStoreSummaryGrandTotal(): { revenue: number; expenses: number; net: number } {
        return {
            revenue:  this.storeGroups.reduce((s, g) => s + g.storeSummary.revenue,  0),
            expenses: this.storeGroups.reduce((s, g) => s + g.storeSummary.expenses, 0),
            net:      this.storeGroups.reduce((s, g) => s + g.storeSummary.net,      0),
        };
    }

    getStoreDetailedGrandTotal(sg: StoreReportGroup): any {
        return {
            xMainAmt:    sg.detailedGroups.reduce((s, g) => s + (g.subtotal.xMainAmt    || 0), 0),
            xIncSurcAmt: sg.detailedGroups.reduce((s, g) => s + (g.subtotal.xIncSurcAmt || 0), 0),
            xTipAmt:     sg.detailedGroups.reduce((s, g) => s + (g.subtotal.xTipAmt     || 0), 0),
            xTtlAmt:     sg.detailedGroups.reduce((s, g) => s + (g.subtotal.xTtlAmt     || 0), 0),
            xMerAmt:     sg.detailedGroups.reduce((s, g) => s + (g.subtotal.xMerAmt     || 0), 0),
        };
    }

    exportPdf(): void {
        this.isPdfLoading = true;
        const domClone = this.reportResults.nativeElement.cloneNode(true);
        let printSection = document.getElementById('printSection');
        if (!printSection) {
            printSection = document.createElement('div');
            printSection.id = 'printSection';
            document.body.appendChild(printSection);
        }
        printSection.innerHTML = '';
        printSection.appendChild(domClone);
        print();
        this.isPdfLoading = false;
    }

    exportCsv(): void {
        this.isCsvLoading = true;
        const filename = `consolidated-report-${this.businessName || this.businessID}.csv`;
        const lines: string[] = [];

        this.storeGroups.forEach(sg => {
            lines.push('');
            lines.push(`=== ${sg.storeName} ===`);

            lines.push('Profit Center,Total Revenue(Sale),Total Expenses(Refund),Net Total');
            sg.summaryRows.forEach(row => {
                lines.push(`"${row.name}",$${row.revenue.toFixed(2)},$${row.expenses.toFixed(2)},$${row.net.toFixed(2)}`);
            });
            lines.push(`"TOTAL",$${sg.storeSummary.revenue.toFixed(2)},$${sg.storeSummary.expenses.toFixed(2)},$${sg.storeSummary.net.toFixed(2)}`);

            lines.push('');

            lines.push('Profit Center,Type,Invoice,Customer #,Card,Account #,Amt,SurChg,Tip,TtlAmt,MerAmt,Auth Date,Batch Date');
            sg.detailedGroups.forEach(group => {
                group.txns.forEach((t: any) => {
                    lines.push([
                        `"${group.pctrName}"`,
                        t.xTxnActTag || '',
                        t.xInvNum || '',
                        t.xCustNum || '',
                        t.xBrandTag || '',
                        t.xMaskedAcctNum || '',
                        t.xMainAmt || 0,
                        t.xIncSurcAmt || 0,
                        t.xTipAmt || 0,
                        t.xTtlAmt || 0,
                        t.xMerAmt || 0,
                        `"${t.xAuthDateTime || ''}"`,
                        `"${t.xSettleDateTime || ''}"`,
                    ].join(','));
                });
                const s = group.subtotal;
                lines.push(`"${group.pctrName} - Subtotal",,,,,,$${s.xMainAmt.toFixed(2)},$${s.xIncSurcAmt.toFixed(2)},$${s.xTipAmt.toFixed(2)},$${s.xTtlAmt.toFixed(2)},$${s.xMerAmt.toFixed(2)},,`);
            });
            const gtd = this.getStoreDetailedGrandTotal(sg);
            lines.push(`"GRAND TOTAL",,,,,,$${gtd.xMainAmt.toFixed(2)},$${gtd.xIncSurcAmt.toFixed(2)},$${gtd.xTipAmt.toFixed(2)},$${gtd.xTtlAmt.toFixed(2)},$${gtd.xMerAmt.toFixed(2)},,`);
        });

        const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.setAttribute('download', filename);
        a.click();
        URL.revokeObjectURL(url);
        this.isCsvLoading = false;
    }

    exportExcel(): void {
        this.isExcelLoading = true;
        const filename = `consolidated-report-${this.businessName || this.businessID}.xlsx`;
        const wb = XLSX.utils.book_new();

        const usedNames = new Set<string>();
        const safeSheetName = (name: string): string => {
            let base = name.substring(0, 31);
            let candidate = base;
            let i = 1;
            while (usedNames.has(candidate)) {
                const suffix = `(${i++})`;
                candidate = base.substring(0, 31 - suffix.length) + suffix;
            }
            usedNames.add(candidate);
            return candidate;
        };

        // ── Sheet 1: Summary (one row per store) ──────────────────────────
        const summaryRows: any[][] = [];
        summaryRows.push(['Store Name', 'Total Revenue(Sale)', 'Total Expenses(Refund)', 'Net Total']);
        this.storeGroups.forEach(sg => {
            summaryRows.push([
                sg.storeName,
                '$' + sg.storeSummary.revenue.toFixed(2),
                '$' + sg.storeSummary.expenses.toFixed(2),
                '$' + sg.storeSummary.net.toFixed(2),
            ]);
        });
        const gTotal = this.getStoreSummaryGrandTotal();
        summaryRows.push([
            'TOTAL',
            '$' + gTotal.revenue.toFixed(2),
            '$' + gTotal.expenses.toFixed(2),
            '$' + gTotal.net.toFixed(2),
        ]);
        const summaryWS = XLSX.utils.aoa_to_sheet(summaryRows);
        autoFitColumns(summaryWS, summaryRows, { currencyColumns: [1, 2, 3] });
        XLSX.utils.book_append_sheet(wb, summaryWS, safeSheetName('Summary'));

        // ── One sheet per store ────────────────────────────────────────────
        this.storeGroups.forEach(sg => {
            const rows: any[][] = [];

            // Summary section
            rows.push(['Profit Center', 'Total Revenue(Sale)', 'Total Expenses(Refund)', 'Net Total']);
            sg.summaryRows.forEach(row => {
                rows.push([
                    row.name,
                    '$' + row.revenue.toFixed(2),
                    '$' + row.expenses.toFixed(2),
                    '$' + row.net.toFixed(2),
                ]);
            });
            rows.push([
                'TOTAL',
                '$' + sg.storeSummary.revenue.toFixed(2),
                '$' + sg.storeSummary.expenses.toFixed(2),
                '$' + sg.storeSummary.net.toFixed(2),
            ]);

            rows.push([]);

            // Detailed section
            rows.push(['Profit Center', 'Type', 'Invoice', 'Customer #', 'Card', 'Account #', 'Amt', 'SurChg', 'Tip', 'TtlAmt', 'MerAmt', 'Auth Date', 'Batch Date']);
            sg.detailedGroups.forEach(group => {
                group.txns.forEach((t: any) => {
                    rows.push([
                        group.pctrName,
                        t.xTxnActTag || '',
                        t.xInvNum || '',
                        t.xCustNum || '',
                        t.xBrandTag || '',
                        t.xMaskedAcctNum || '',
                        t.xMainAmt || 0,
                        t.xIncSurcAmt || 0,
                        t.xTipAmt || 0,
                        t.xTtlAmt || 0,
                        t.xMerAmt || 0,
                        t.xAuthDateTime || '',
                        t.xSettleDateTime || '',
                    ]);
                });
                const s = group.subtotal;
                rows.push([
                    `${group.pctrName} - Subtotal`, '', '', '', '', '',
                    '$' + s.xMainAmt.toFixed(2), '$' + s.xIncSurcAmt.toFixed(2),
                    '$' + s.xTipAmt.toFixed(2),  '$' + s.xTtlAmt.toFixed(2),
                    '$' + s.xMerAmt.toFixed(2),  '', '',
                ]);
            });
            const gtd = this.getStoreDetailedGrandTotal(sg);
            rows.push([
                'GRAND TOTAL', '', '', '', '', '',
                '$' + gtd.xMainAmt.toFixed(2),    '$' + gtd.xIncSurcAmt.toFixed(2),
                '$' + gtd.xTipAmt.toFixed(2),     '$' + gtd.xTtlAmt.toFixed(2),
                '$' + gtd.xMerAmt.toFixed(2),     '', '',
            ]);

            const ws = XLSX.utils.aoa_to_sheet(rows);
            autoFitColumns(ws, rows, { currencyColumns: [6, 7, 8, 9, 10] });
            XLSX.utils.book_append_sheet(wb, ws, safeSheetName(sg.storeName));
        });

        XLSX.writeFile(wb, filename);
        this.isExcelLoading = false;
    }
}
